import {
    ActivityLogWithDetail,
    CommentWithMentionsAndUser,
} from '@/common/types/databaseTypes';
import { supabase, withSafeUsers } from '@tickitacka/shared';
import i18next from '@/translation/i18n';

export const activityService = {
    async fetchActivityLogs(goalId: string): Promise<ActivityLogWithDetail[]> {
        const { data: activityLogs, error } = await supabase
            .from('activity_log')
            .select(
                `
                  *,
                  user:user_id(id, name, profile_image_url)
                `
            )
            .eq('goal_id', goalId)
            .or(
                'activity_type.neq.comment,and(activity_type.eq.comment,action.neq.delete)'
            )
            .order('created_at', { ascending: false });

        // 타입 단언으로 타입 오류 해결
        const typedLogs = activityLogs as ActivityLogWithDetail[];

        if (error) {
            console.error('활동 로그 조회 실패:', error);
            return [];
        }

        // 각 활동 유형별로 추가 데이터 조회
        const enhancedLogs = await Promise.all(
            typedLogs.map(async log => {
                if (log.activity_type === 'goal') {
                    // 목표 활동인 경우 goal_history 데이터 조회
                    const { data: goalHistory } = await supabase
                        .from('goal_history')
                        .select('*')
                        .eq('id', log.reference_id)
                        .eq('enable', true)
                        .maybeSingle();

                    if (!goalHistory) return log;

                    return {
                        ...log,
                        goal: goalHistory,
                    } as ActivityLogWithDetail;
                } else if (log.activity_type === 'comment') {
                    // 댓글의 경우 원본 댓글 데이터와 대댓글들을 함께 조회
                    const { data } = await supabase
                        .from('comment')
                        .select(
                            `
                              *,
                              user:register_id(id, name, profile_image_url),
                              mentions:comment_mention!comment_id (
                                  id,
                                  user_id,
                                  comment_id,
                                  mention_user:user!user_id (
                                      id,
                                      name,
                                      profile_image_url
                                  )
                              )
                          `
                        )
                        .eq('id', log.reference_id)
                        .eq('enable', true)
                        .maybeSingle();

                    if (!data) return log;

                    const { data: replies } = await supabase
                        .from('comment')
                        .select(
                            `
                              *,
                              user:register_id(id, name, profile_image_url),
                              mentions:comment_mention!comment_id (
                                  id,
                                  user_id,
                                  comment_id,
                                  mention_user:user!user_id (
                                      id,
                                      name,
                                      profile_image_url
                                  )
                              )
                            `
                        )
                        .eq('parent_id', log.reference_id)
                        .eq('enable', true)
                        .order('register_date', { ascending: true });

                    // 탈퇴한 사용자 처리
                    const t = (key: string) => i18next.t(key);
                    let comment = withSafeUsers(
                        data as CommentWithMentionsAndUser,
                        ['user', 'mentions'],
                        t
                    );

                    // 답글도 처리
                    if (replies && replies.length > 0) {
                        const safeReplies = replies.map(reply =>
                            withSafeUsers(
                                reply as CommentWithMentionsAndUser,
                                ['user', 'mentions'],
                                t
                            )
                        );
                        comment = { ...comment, children: safeReplies };
                    } else {
                        comment = { ...comment, children: [] };
                    }

                    return {
                        ...log,
                        comment,
                    } as ActivityLogWithDetail;
                } else if (log.activity_type === 'note') {
                    // 노트의 경우 원본 노트 데이터 조회
                    const { data: note } = await supabase
                        .from('note')
                        .select('*')
                        .eq('id', log.reference_id)
                        .eq('enable', true)
                        .maybeSingle();

                    if (!note) return log;

                    return {
                        ...log,
                        note,
                    } as ActivityLogWithDetail;
                }
                return log;
            })
        );

        // 최상위 activity_log의 user 필드도 처리
        const t = (key: string) => i18next.t(key);
        const safeEnhancedLogs = enhancedLogs.map(log =>
            withSafeUsers(log, ['user'], t)
        );

        return safeEnhancedLogs;
    },

    async deleteActivityLog({
        activityType,
        referenceId,
    }: {
        activityType: string;
        referenceId: string;
    }): Promise<void> {
        const { error } = await supabase
            .from('activity_log')
            .delete()
            .eq('activity_type', activityType)
            .eq('reference_id', referenceId);

        if (error) throw error;
        return;
    },
};
